/* eslint-disable react/jsx-props-no-spreading */

/*
For use add this on data.json
{
  "type": "SelectSearch",
  "name": "dermatologist",
  "label": "pages.form.fields.selectSearch.label",
  "placeholder": "pages.form.fields.selectSearch.placeholder",
  "info": "pages.form.fields.selectSearch.info",
  "messages": {
    "noResults": "pages.form.fields.selectSearch.messages.noResults",
    "needFilter": "pages.form.fields.selectSearch.messages.needFilter",
    "searching": "pages.form.fields.selectSearch.messages.searching"
  },
  "filter": {
    "name": "zipCode",
    "label": "pages.form.fields.selectSearch.filter.label",
    "filterBy": "zip"
  },
  "optional": true
},
*/

import React, { useState } from 'react'
import styled from 'styled-components'
import { useStoreState, useStoreActions } from 'easy-peasy'
import Select, { components } from 'react-select'

import ErrorMessage from './ErrorMessage'
import Text, { useTranslations } from '../../utils/Text'
import Search from '../icons/Search'

const Container = styled.div`
  margin: 24px auto 44px;
  text-align: left;
`

const Wrapper = styled.div`
  max-width: 443px;
  margin: 0 auto;
  position: relative;
  width: 100%;
  color: #818181;
`

const Input = styled.input`
  border-radius: 0;
  font-size: 16px !important;
  width: 100%;
  height: 49px;
  border: none;
  border-bottom: 1px solid #818181 !important;
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  padding-left: 10px;
  padding-right: 10px;
  &:focus {
    border-bottom: 1px solid #414141 !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    outline: none;
    box-shadow: none;
  }
`

const Label = styled.label`
  position: absolute;
  pointer-events: none;
  font-size: 16px;
  left: 10px;
  top: 10px;
  transition: 0.3s ease all;
  ${Input}:focus ~ & {
    top: -12px;
    font-size: 12px;
  }
  ${Input}:not([value=""]) ~ & {
    top: -12px;
    font-size: 12px;
  }
`

const OptionContainer = styled.div`
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #414141;
  &:not(:last-child) {
    border-bottom: 1px solid #ccc;
  }
  &:hover {
    background-color: #f7f7f6;
  }
  .option-wrapper {
    padding: 10px 22px;
    h5 {
      font-size: 16px !important;
      line-height: 1.8 !important;
    }
    p {
      line-height: 1.5 !important;
    }
  }
`

const InfoWrapper = styled.div`
  padding-top: 24px;
  padding-bottom: 14px;
`

const customStyles = {
  input: base => ({
    ...base,
    padding: '0',
    margin: '0',
  }),
  control: base => ({
    ...base,
    minHeight: '49px',
    maxHeight: '49px',
    border: 'none',
    borderRadius: '0',
    borderBottom: '1px solid #818181 !important',
    fontSize: '16px',
    fontWeight: 'normal',
    color: '#414141',
    boxShadow: 'none',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: base => ({
    ...base,
    backgroundColor: '#414141',
    minWidth: '40px',
    marginTop: '9px',
    display: 'flex',
    justifyContent: 'center',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: base => ({
    ...base,
    top: '41px',
    boxShadow: 'none',
    borderRadius: '0',
    border: '1px solid #818181',
    borderTop: 'none',
  }),
  menuList: base => ({
    ...base,
    marginTop: '0',
    padding: '0',
  }),
  valueContainer: base => ({
    ...base,
    textAlign: 'left',
    fontSize: '16px',
    color: '#818181',
    overflow: 'visible',
  }),
  singleValue: base => ({
    ...base,
    textAlign: 'left',
    fontSize: '16px',
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: '12px',
  }),
  placeholder: (base, { hasValue, selectProps }) => ({
    ...base,
    position: 'absolute',
    top: hasValue || selectProps.inputValue ? '-24px' : '0',
    fontSize: hasValue || selectProps.inputValue ? '12px' : '16px',
    transition: '0.2s ease all',
  }),
}

const CustomDropdownIndicator = ({ innerRef, innerProps }) => (
  <span
    role="button"
    aria-label="search"
    className="dropdown-icon"
    ref={innerRef}
    {...innerProps}
    style={{ padding: '8px' }}
  >
    <Search color="white" />
  </span>
)

const CustomOption = props => {
  const { data, innerRef, innerProps } = props
  return (
    <OptionContainer ref={innerRef} {...innerProps}>
      <div className="option-wrapper">
        <h5>{data.label}</h5>
        <p>{data.street}</p>
        <p>{data.zip}</p>
        <p>{data.city}</p>
      </div>
    </OptionContainer>
  )
}

const CustomValueContainer = ({ children, ...props }) => {
  const { ValueContainer, Placeholder } = components
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(children, child =>
        child && child.type !== Placeholder ? child : null,
      )}
    </ValueContainer>
  )
}

export default function SelectSearch({ name, ...props }) {
  const { optional } = props
  const [
    { label },
    error,
    { dermatologists },
  ] = useStoreState(state => [
    state.form.getByName(name),
    state.form.getErrorByName(name),
    state.extraData,
  ])

  const [setInputValue] = useStoreActions(actions => [
    actions.form.setInputValue,
  ])

  const [valueFilterInput, setValueFilterInput] = useState('')
  const [value, setValue] = useState({})

  const selectLabel = useTranslations(label, {})
  const noResults = useTranslations(props.messages.noResults, {})
  const needFilter = useTranslations(props.messages.needFilter, {})

  const isOptional = useTranslations(props.label, { optional })
  if (optional && isOptional === undefined) return null

  const filterOption = ({ data }, string) => {
    if (valueFilterInput !== data[props.filter.filterBy]) return false
    if (string)
      return Object.values(data)
        .toString()
        .toLowerCase()
        .includes(string.toLowerCase())
    return true
  }

  // validate zipcode usa with regex
  const validateWithRegex = string => {
    const regex = /^\d{5}[-\s]?(?:\d{4})?$/gm
    return regex.test(string)
  }

  return (
    <>
      <Container>
        {props.info && (
          <InfoWrapper>
            <Text id={props.info} />
          </InfoWrapper>
        )}
        {props.filter && (
          <Container>
            <Wrapper>
              <Input
                id={`text-${name}`}
                name={name}
                value={valueFilterInput}
                type="text"
                onChange={({ target: { value: val } }) => {
                  setValueFilterInput(val)
                }}
                // onBlur={() => {
                //   setInputValue({ name, valueFilterInput })
                // }}
              />
              <Label htmlFor={`text-${name}`}>
                <Text id={props.filter.label} />
              </Label>
              <ErrorMessage
                error={error}
                id={`error-${name}`}
                className="error"
              />
            </Wrapper>
          </Container>
        )}
        {props.filter &&
        valueFilterInput > 0 &&
        validateWithRegex(valueFilterInput) ? (
          <Wrapper>
            <Select
              // defaultMenuIsOpen
              // isClearable
              styles={customStyles}
              options={dermatologists}
              filterOption={props.filter && filterOption}
              noOptionsMessage={() => {
                if (!valueFilterInput) return needFilter
                return noResults
              }}
              placeholder={selectLabel}
              components={{
                Option: CustomOption,
                DropdownIndicator: CustomDropdownIndicator,
                ValueContainer: CustomValueContainer,
              }}
              onChange={target => {
                setValue({ zip: target.zip, name: target.label })
              }}
              onBlur={() => {
                if (value) {
                  setInputValue({ name, value })
                }
              }}
            />
          </Wrapper>
        ) : null}
      </Container>
    </>
  )
}
